import { useEffect, useState } from 'react';
import { ShieldCheck, ScrollText } from 'lucide-react';
import { apiFetch } from './apiBase';

const ROLES = ['viewer', 'consultant', 'admin'];

export default function AdminPanel() {
  const [users, setUsers] = useState(null);
  const [audit, setAudit] = useState(null);
  const [error, setError] = useState('');

  const loadUsers = () =>
    apiFetch('/api/admin/users')
      .then((d) => setUsers(d.users))
      .catch((e) => setError(e.message));
  const loadAudit = () =>
    apiFetch('/api/audit?limit=100')
      .then((d) => setAudit(d.rows))
      .catch((e) => setError(e.message));

  useEffect(() => {
    loadUsers();
    loadAudit();
  }, []);

  const changeRole = async (id, role) => {
    setError('');
    try {
      await apiFetch(`/api/admin/users/${id}/role`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
      });
      await loadUsers();
      await loadAudit();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="space-y-6">
      <header className="page-hero">
        <p className="section-title">Administration</p>
        <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-100">Admin</h2>
        <p className="mt-2 max-w-2xl text-sm text-slate-400">
          Manage user roles and review the security audit trail.
        </p>
      </header>

      {error && <p className="alert-error text-sm">{error}</p>}

      <div className="card-panel p-5">
        <div className="mb-4 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-cyan-500" />
          <h3 className="font-semibold text-slate-100">Users &amp; roles</h3>
        </div>
        {!users ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-slate-400">
                  <th className="py-2 pr-4">User</th>
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Role</th>
                  <th className="py-2 pr-4">MFA</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-slate-700/50">
                    <td className="py-2 pr-4 font-medium text-slate-200">{u.username}</td>
                    <td className="py-2 pr-4 text-slate-400">{u.email || '—'}</td>
                    <td className="py-2 pr-4">
                      <select
                        value={u.role}
                        onChange={(e) => changeRole(u.id, e.target.value)}
                        className="input-field max-w-[10rem] py-1"
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 pr-4">
                      {u.mfaEnabled ? (
                        <span className="text-emerald-400">On</span>
                      ) : (
                        <span className="text-slate-500">Off</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card-panel p-5">
        <div className="mb-4 flex items-center gap-2">
          <ScrollText className="h-5 w-5 text-cyan-500" />
          <h3 className="font-semibold text-slate-100">Audit log</h3>
        </div>
        {!audit ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : audit.length === 0 ? (
          <p className="text-sm text-slate-400">No events yet.</p>
        ) : (
          <div className="max-h-[28rem] overflow-auto">
            <table className="w-full text-left text-xs">
              <thead className="sticky top-0 bg-slate-900/80">
                <tr className="text-slate-400">
                  <th className="py-2 pr-3">Time</th>
                  <th className="py-2 pr-3">Actor</th>
                  <th className="py-2 pr-3">Action</th>
                  <th className="py-2 pr-3">Outcome</th>
                  <th className="py-2 pr-3">IP</th>
                  <th className="py-2 pr-3">Target</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((r) => (
                  <tr key={r.id} className="border-t border-slate-700/40">
                    <td className="whitespace-nowrap py-1.5 pr-3 text-slate-400">
                      {new Date(r.createdAt).toLocaleString()}
                    </td>
                    <td className="py-1.5 pr-3 text-slate-300">
                      {r.actorUsername || '—'}
                      {r.actorRole ? ` (${r.actorRole})` : ''}
                    </td>
                    <td className="py-1.5 pr-3 font-mono text-slate-200">{r.action}</td>
                    <td className="py-1.5 pr-3">
                      {r.outcome === 'success' ? (
                        <span className="text-emerald-400">{r.outcome}</span>
                      ) : (
                        <span className="text-amber-400">{r.outcome}</span>
                      )}
                    </td>
                    <td className="py-1.5 pr-3 text-slate-500">{r.ip || ''}</td>
                    <td className="py-1.5 pr-3 text-slate-500">
                      {r.targetType ? `${r.targetType}:${(r.targetId || '').slice(0, 8)}` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
